import React from 'react';
import {
    Button,
    CrossIcon,
    EditIcon,
    IconButton,
    Menu,
    MoreIcon,
    Pane,
    Popover,
    Position,
    Text,
    TextInput,
    toaster,
} from 'evergreen-ui';
import {VirtualList} from 'Utils/VirtualList';
import {createUseStyles} from 'react-jss';
import {Link, useNavigate} from 'react-router-dom';
import SuspenseSpinner from 'Utils/SuspenseSpinner';
import {useDeleteReportMutation, useGetReportsListQuery} from 'Services/api/reportsApi';
import DeleteDialog from 'Utils/DeleteDialog';
import {Sidebar} from 'Shared/Sidebar/Sidebar';
import useSidebar from 'Shared/Sidebar/useSidebar';
import {ReportContext} from './Report.Context';
import {appHomeToggleShowSidebar} from 'Features/App/appSlice';

const useStyles = createUseStyles({
  rowReport: {
    display: 'grid',
    alignItems: 'center',
    borderBottom: '1px solid rgb(237, 240, 242)',
    gridTemplateColumns: '1fr repeat(2, max-content)',
    padding: '.3em',
    boxSizing: 'border-box',
    cursor: 'pointer',
    textDecoration: 'none',
    backgroundColor: '#fff',
    '&:hover': {
      backgroundColor: 'rgba(67, 90, 111, 0.04)',
    },
  },
  active: {
    backgroundColor: 'rgba(16, 112, 202, 0.06)',
  },
});

const ReportsTable = React.memo(({ data, row, isFetching }) => {
  if (isFetching) return <SuspenseSpinner />;
  if (data.length === 0) return <Text>Нет отчетов</Text>;
  return <VirtualList data={data} row={row} />;
});
ReportsTable.displayName = 'ReportsTable';

 export default function ReportsSidebar(canDownloadSelected, onChange, downloadAllSelectedReports) {
  const { isPinned, isHide, onChangeHide, onChangePinned } = useSidebar();
  const { selectedReportId, reset } = React.useContext(ReportContext);
  const classes = useStyles();
  const navigate = useNavigate();
  const { data: reports = [], isFetching: isFetchingReports } = useGetReportsListQuery();
  const [searchText, setSearchText] = React.useState('');
  const [deleteDialogData, setDeleteDialogData] = React.useState(null);
  const [deleteReport] = useDeleteReportMutation();

  const clickCreateReport = () => {
     reset();
     navigate('/reports/new');
  };

  const onDeleteReport = React.useCallback(async () => {
     await deleteReport(deleteDialogData.id);
     toaster.success('Удалено');
     setDeleteDialogData(null);
     navigate('/reports');
  }, [deleteDialogData, deleteReport, navigate]);

  const filteredReports = React.useMemo(() => reports.filter((report) => {
    if (searchText === '') return true;
    return report.name.toUpperCase().includes(searchText.toUpperCase());
  }), [reports, searchText]);

  const Row = React.useCallback((report) => {
      return <Pane display={"flex"}>
          <input
              type={"checkbox"}
              onChange={(e) => onChange(report, e)}
          >
          </input>
          <Pane
              width={"96%"}
              className={`${classes.rowReport}${report.id === selectedReportId ? ` ${classes.active}` : ''}`}
              is={Link}
              to={`${report.id}/table`}
              onClick={(e) => {
                  if (['svg', 'button', 'path'].includes(e.target.tagName.toLowerCase())) {
                      e.preventDefault();
                      e.stopPropagation();
                  }
              }}
          >
              <Text wordBreak="break-word">{report.name}</Text>
              <Popover
                  position={Position.RIGHT}
                  content={
                      ({close}) => (
                          <Menu>
                              <Menu.Group>
                                  <Menu.Item
                                      is={Link}
                                      to={`${report.id}/edit`}
                                      onSelect={(e) => {
                                          e.stopPropagation();
                                          close();
                                      }}
                                      icon={EditIcon}
                                  >
                                      Редактировать
                                  </Menu.Item>
                                  <Menu.Divider/>
                                  <Menu.Item
                                      intent="danger"
                                      icon={CrossIcon}
                                      onSelect={(e) => {
                                          e.stopPropagation();
                                          deleteReport(report.id);
                                          close();
                                      }}
                                  >
                                      Удалить
                                  </Menu.Item>
                              </Menu.Group>
                          </Menu>
                      )
                  }
              >
                  <IconButton
                      appearance="minimal"
                      icon={MoreIcon}
                  />
              </Popover>
          </Pane></Pane>
  }, [classes.active, classes.rowReport, selectedReportId]);

  return (
      <Sidebar
          isPinned={isPinned}
          isHide={isHide}
          onClickHide={onChangeHide}
          onClickPin={onChangePinned}
          title="Отчеты"
          width={400}
          containerStyles={{
              display: 'grid',
              padding: '.5rem',
          }}
      >
          <Pane display="grid" gridTemplateRows="max-content 1fr max-content" rowGap=".5rem" zIndex={2}>
              <Pane display="grid" gridTemplateColumns="1fr max-content" columnGap=".5rem">
                  <TextInput
                      width="100%"
                      placeholder="Введите название отчета"
                      value={searchText}
                      onChange={(e) => setSearchText(e.target.value)}
                  />
                  <Button
                      intent="success"
                      appearance="primary"
                      disabled={!canDownloadSelected}
                      onClick={() => downloadAllSelectedReports()}
                  >
                      Загрузить выбранное
                  </Button>
              </Pane>
              <Pane>
                  <ReportsTable data={filteredReports} row={Row} isFetching={isFetchingReports} />
              </Pane>
              <Text>
                  {`Отчеты: ${filteredReports?.length} из ${reports?.length}`}
              </Text>
          </Pane>
          {
        deleteDialogData && (
          <DeleteDialog
            title="Подтвердите удаление"
            closeDialog={() => setDeleteDialogData(null)}
            onConfirm={onDeleteReport}
          >
            {`Удалить отчет "${deleteDialogData.name}"?`}
          </DeleteDialog>
        )
      }
    </Sidebar>
  );
}